import { useCallback, useState } from 'react';

import { ILocation } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { FilterRestaurantType, OperationalStatus } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { useNearbyRestaurants } from 'pages/store-locator/hooks/helpers/use-restaurants';
import { ISearchNearbyRestaurants } from 'pages/store-locator/hooks/types';
import { useGeolocation } from 'state/geolocation';
import { ServiceMode } from 'state/order';
import { useStoreContext } from 'state/store';
import { usePosDataQuery } from 'state/store/hooks/use-pos-data-query';
import { getUnavailableCartEntries } from 'utils/availability';
import { CartEntryType } from 'utils/cart';
import { isValidPosition } from 'utils/geolocation';

import { NearbyRestaurantSearchResult } from '../types';

export const useRestaurantSearch = (skipRestaurantId?: string) => {
  const [availableRestaurant, setAvailableRestaurant] = useState<IRestaurant | null>(null);

  const { isPermissionGranted, userCoordinates } = useGeolocation();
  const { store: closedStore } = useStoreContext();
  const [isLoading, setIsLoading] = useState(false);
  const { fetch: fetchNearbyRestaurants } = useNearbyRestaurants();
  const { refetch: refetchPosData } = usePosDataQuery({ lazy: true, storeNumber: '' });
  const { cartEntries } = useCart();

  // If we have permission and user coordinates, use them. Otherwise, use the closed store coordinates.
  const coordinatesToUse: ILocation = {
    lat: isPermissionGranted && userCoordinates ? userCoordinates.lat : closedStore.latitude || 0,
    lng: isPermissionGranted && userCoordinates ? userCoordinates.lng : closedStore.longitude || 0,
  };

  const searchNearbyRestaurantsAsync = useCallback(
    async ({ location }: ISearchNearbyRestaurants): Promise<IRestaurant[]> => {
      if (!skipRestaurantId && !isValidPosition(location)) {
        return [];
      }

      const nearbyRestaurants = ((await fetchNearbyRestaurants(
        {
          coordinates: {
            userLat: Number(location.lat.toFixed(3)),
            userLng: Number(location.lng.toFixed(3)),
            searchRadius: 15,
          },
          first: 10,
          filter: FilterRestaurantType.NEARBY,
          status: OperationalStatus.OPEN,
          radiusStrictMode: true,
        },
        { allowFromCache: false }
      )) as unknown) as IRestaurant[];

      return nearbyRestaurants.filter(restaurant => restaurant._id !== skipRestaurantId) ?? [];
    },
    [fetchNearbyRestaurants, skipRestaurantId]
  );

  const checkRestaurantAvailability = async (
    restaurant: IRestaurant
  ): Promise<NearbyRestaurantSearchResult> => {
    try {
      const selectedStorePrices = await refetchPosData({
        storeNumber: restaurant.number || null,
        isDelivery: false,
      });

      const mappedCartEntries = (cartEntries || []).map(cartEntry => ({
        ...cartEntry,
        type:
          cartEntry.type === CartEntryType.offerCombo
            ? CartEntryType.combo
            : cartEntry.type === CartEntryType.offerItem
            ? CartEntryType.item
            : cartEntry.type,
      }));

      const unavailableItems = getUnavailableCartEntries(
        mappedCartEntries,
        restaurant,
        selectedStorePrices?.posData || {},
        ServiceMode.DRIVE_THRU
      );

      return {
        restaurant,
        isAvailable: unavailableItems.length === 0,
        unavailableItems,
      };
    } catch (error) {
      return {
        restaurant: null,
        isAvailable: false,
        unavailableItems: cartEntries || [],
      };
    }
  };

  const searchOneAvailableNearbyRestaurant = async (): Promise<IRestaurant | null> => {
    setIsLoading(true);
    const nearbyRestaurants = await searchNearbyRestaurantsAsync({
      location: coordinatesToUse,
    });
    for (const restaurant of nearbyRestaurants ?? []) {
      const result = await checkRestaurantAvailability(restaurant);
      if (result.isAvailable) {
        setAvailableRestaurant(restaurant);
        return restaurant;
      }
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    setIsLoading(false);
    return null;
  };
  return { searchOneAvailableNearbyRestaurant, isLoading, availableRestaurant };
};
